import httpClient from './httpClient';
import axios, { AxiosRequestConfig } from 'axios';

interface UploadResponse {
  success: boolean;
  data?: any;
  error?: {
    message: string;
  };
}

// Course Thumbnail Upload
export const uploadCourseThumbnail = async (file: File, courseId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('thumbnail', file);
    formData.append('courseId', courseId.toString());

    // httpClient.post already returns the response BODY; this endpoint's body is
    // { success, message, filename } (no nested `data`), so return `response`.
    const response = await httpClient.post('/content/uploads/course/thumbnail', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return { success: true, data: response, error: undefined };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload thumbnail' },
    };
  }
};

// Course Overview Video Upload
export const uploadCourseOverviewVideo = async (file: File, courseId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('courseId', courseId.toString());

    const response = await httpClient.post('/content/uploads/course/overview/video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // httpClient.post already returns the response BODY (no nested `data` here).
    return {
      success: true,
      data: response,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload overview video' },
    };
  }
};

// Course Overview SCORM Upload
export const uploadCourseOverviewScorm = async (file: File, courseId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('scorm', file);
    formData.append('courseId', courseId.toString());

    const response = await httpClient.post('/content/uploads/course/overview/scorm', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // httpClient.post already returns the response BODY (no nested `data` here).
    return {
      success: true,
      data: response,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload SCORM package' },
    };
  }
};

// Lesson SCORM Upload
export const uploadLessonScorm = async (file: File, lessonId: number): Promise<UploadResponse> => {
  try {
    console.log('Frontend - Starting SCORM upload:', {
      filename: file.name,
      size: file.size,
      type: file.type,
      lessonId,
    });

    const formData = new FormData();
    formData.append('scorm', file);
    formData.append('lessonId', lessonId.toString());

    const token = localStorage.getItem('authToken');
    const axiosConfig: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      timeout: 10 * 60 * 1000, // 10 minutes timeout
      onUploadProgress: (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`SCORM upload progress: ${percentCompleted}%`);
      },
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/content/uploads/lesson/scorm`,
      formData,
      axiosConfig
    );

    console.log('Frontend - SCORM upload successful:', response.data);

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    console.error('Frontend - SCORM upload error:', error);

    let errorMessage = 'Failed to upload lesson SCORM';

    if (error.code === 'ECONNRESET') {
      errorMessage = 'Connection was reset during upload. The file might be too large or the server timed out.';
    } else if (error.code === 'ETIMEDOUT') {
      errorMessage = 'Upload timed out. Please try with a smaller file.';
    } else if (error.response?.status === 413) {
      errorMessage = 'File is too large. Maximum size is 500MB.';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    return {
      success: false,
      error: { message: errorMessage },
    };
  }
};

// Lesson Document Upload
export const uploadLessonDocument = async (file: File, lessonId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('lessonId', lessonId.toString());

    const response = await httpClient.post('/content/uploads/lesson/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload lesson document' },
    };
  }
};

// Lesson MP4 Video Upload
export const uploadLessonMp4 = async (file: File, lessonId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('lessonId', lessonId.toString());

    const response = await httpClient.post('/content/uploads/lesson/mp4', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload lesson video' },
    };
  }
};

// Certification Template Upload (Admin/Super Admin)
export const uploadCertificationTemplate = async (file: File): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('template', file);

    // httpClient.post already returns the response BODY (it unwraps axios's
    // `.data`). This endpoint's body is { success, message, filename } with no
    // nested `data` key, so reading `response.data` here would be undefined —
    // return `response` itself.
    const response = await httpClient.post('/content/uploads/certification/template', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return { success: true, data: response, error: undefined };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload certification template' },
    };
  }
};

// Event Thumbnail Upload
export const uploadEventThumbnail = async (file: File, eventId: number): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('thumbnail', file);
    formData.append('eventId', eventId.toString());

    const response = await httpClient.post('/content/uploads/event/thumbnail', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // httpClient.post already returns the response BODY (no nested `data` here).
    return {
      success: true,
      data: response,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to upload event thumbnail' },
    };
  }
};
